import type { PendingWork as PendingWorkRecord } from 'src/types/editorial';

/**
 * Why the rest of this broadcast is not here yet.
 *
 * Art Direction §7: absence is a state, not a blank. A stage that produced nothing because no
 * model would answer is a different fact from one that ran and found nothing, and drawing both as
 * an empty area teaches an editor that the tool is unreliable — which is what happened. The queue
 * already knew: the job row names the stages it is waiting to run and carries the sentence saying
 * why. Nothing read it back onto the broadcast until now.
 *
 * On the stream shell rather than inside one workspace, because the answer is the same on Temas,
 * Claims, Relevancia and Historias, and an editor should not have to visit four empty screens to
 * learn it once.
 */
const STAGE_LABEL: Record<string, string> = {
  transcript: 'Guion',
  topics: 'Temas',
  claims: 'Afirmaciones',
  prenota: 'Historias',
  x_review: 'Relevancia',
  notas: 'Notas',
};

export function PendingWork({ work }: { work: PendingWorkRecord | null }) {
  if (!work || work.stages.length === 0) return null;

  const stages = work.stages.map(stage => STAGE_LABEL[stage] ?? stage).join(', ');
  const running = work.status === 'running';

  return (
    <div className="pending-work" data-status={work.status} role="status">
      <p className="pending-work__what">
        <strong>{stages}</strong> {running ? 'se están generando ahora.' : 'siguen en cola para esta transmisión.'}
      </p>
      {/* The queue's own sentence when it recorded one — it is more specific than anything this
          component could invent, and it is what changes when the situation does. */}
      <p className="pending-work__why">
        {work.reason
          ? work.reason
          : running
            ? 'La máquina las está procesando; las secciones se llenan conforme terminan.'
            : 'La máquina las tomará en cuanto esté disponible. Las secciones vacías de abajo son estas etapas, no un resultado.'}
      </p>
    </div>
  );
}
